import { keyframes } from '@emotion/react';
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Circle,
  Collapse,
  Flex,
  HStack,
  Icon,
  IconButton,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '@chakra-ui/icons';
import { FiShare } from 'react-icons/fi';
import { isInstalledAppRuntime } from 'utils/pwaRuntime';
import { MdInstallMobile, MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import {
  getPwaInstallCoachmarkState,
  hidePwaInstallCoachmark,
  markPwaInstalled,
} from 'utils/pwaInstallCoachmarkCookie';

type DeferredInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

type PWAInstallCoachmarkProps = {
  enabled?: boolean;
};

type SupportedLocale = 'es' | 'en' | 'fr' | 'pt' | 'it' | 'de';

type CoachmarkCopy = {
  badge: string;
  title: string;
  subtitle: string;
  installButton: string;
  dismiss: string;
  later: string;
  details: string;
  hideDetails: string;
  safariHint: string;
  installedHint: string;
  nativeHint: string;
  stepSafari: string;
  stepShare: string;
  stepHome: string;
  stepLaunch: string;
};

const PWA_COPY: Record<SupportedLocale, CoachmarkCopy> = {
  es: {
    badge: 'App recomendada',
    title: 'Instala {{appName}}',
    subtitle: 'Acceso mas limpio, pantalla completa y mejor experiencia movil.',
    installButton: 'Instalar',
    dismiss: 'Cerrar',
    later: 'Luego',
    details: 'Ver pasos',
    hideDetails: 'Ocultar pasos',
    safariHint: 'En iPhone se instala desde Safari.',
    installedHint: 'Despues abre {{appName}} desde el icono para evitar la barra del navegador.',
    nativeHint: 'El navegador abrira el dialogo nativo de instalacion.',
    stepSafari: 'Abre esta pagina en Safari.',
    stepShare: 'Pulsa Compartir.',
    stepHome: 'Elige Anadir a pantalla de inicio.',
    stepLaunch: 'Abre {{appName}} desde el nuevo icono.',
  },
  en: {
    badge: 'Recommended app',
    title: 'Install {{appName}}',
    subtitle: 'Cleaner access, full screen, and a better mobile experience.',
    installButton: 'Install',
    dismiss: 'Close',
    later: 'Later',
    details: 'Show steps',
    hideDetails: 'Hide steps',
    safariHint: 'On iPhone, install it from Safari.',
    installedHint: 'Then open {{appName}} from the icon to avoid the browser bar.',
    nativeHint: 'The browser will open the native install prompt.',
    stepSafari: 'Open this page in Safari.',
    stepShare: 'Tap Share.',
    stepHome: 'Choose Add to Home Screen.',
    stepLaunch: 'Open {{appName}} from the new icon.',
  },
  fr: {
    badge: 'App recommandee',
    title: 'Installez {{appName}}',
    subtitle: 'Acces plus propre, plein ecran et meilleure experience mobile.',
    installButton: 'Installer',
    dismiss: 'Fermer',
    later: 'Plus tard',
    details: 'Voir les etapes',
    hideDetails: 'Masquer',
    safariHint: 'Sur iPhone, installez-la depuis Safari.',
    installedHint: 'Ouvrez ensuite {{appName}} depuis son icone pour eviter la barre du navigateur.',
    nativeHint: 'Le navigateur ouvrira la fenetre native d installation.',
    stepSafari: 'Ouvrez cette page dans Safari.',
    stepShare: 'Touchez Partager.',
    stepHome: 'Choisissez Ajouter a l ecran d accueil.',
    stepLaunch: 'Ouvrez {{appName}} depuis la nouvelle icone.',
  },
  pt: {
    badge: 'App recomendada',
    title: 'Instale o {{appName}}',
    subtitle: 'Acesso mais limpo, ecra inteiro e melhor experiencia movel.',
    installButton: 'Instalar',
    dismiss: 'Fechar',
    later: 'Mais tarde',
    details: 'Ver passos',
    hideDetails: 'Ocultar',
    safariHint: 'No iPhone, instale a partir do Safari.',
    installedHint: 'Depois abra o {{appName}} pelo icone para evitar a barra do navegador.',
    nativeHint: 'O navegador abrira o dialogo nativo de instalacao.',
    stepSafari: 'Abra esta pagina no Safari.',
    stepShare: 'Toque em Partilhar.',
    stepHome: 'Escolha Adicionar ao ecra principal.',
    stepLaunch: 'Abra o {{appName}} pelo novo icone.',
  },
  it: {
    badge: 'App consigliata',
    title: 'Installa {{appName}}',
    subtitle: 'Accesso piu pulito, schermo intero e migliore esperienza mobile.',
    installButton: 'Installa',
    dismiss: 'Chiudi',
    later: 'Piu tardi',
    details: 'Mostra passaggi',
    hideDetails: 'Nascondi',
    safariHint: 'Su iPhone installala da Safari.',
    installedHint: 'Poi apri {{appName}} dall icona per evitare la barra del browser.',
    nativeHint: 'Il browser aprira il prompt nativo di installazione.',
    stepSafari: 'Apri questa pagina in Safari.',
    stepShare: 'Tocca Condividi.',
    stepHome: 'Scegli Aggiungi alla schermata Home.',
    stepLaunch: 'Apri {{appName}} dalla nuova icona.',
  },
  de: {
    badge: 'App empfohlen',
    title: '{{appName}} installieren',
    subtitle: 'Sauberer Zugriff, Vollbild und bessere mobile Nutzung.',
    installButton: 'Installieren',
    dismiss: 'Schliessen',
    later: 'Spater',
    details: 'Schritte zeigen',
    hideDetails: 'Ausblenden',
    safariHint: 'Auf dem iPhone installierst du sie aus Safari.',
    installedHint: 'Offne {{appName}} danach uber das Icon, um die Browserleiste zu vermeiden.',
    nativeHint: 'Der Browser offnet den nativen Installationsdialog.',
    stepSafari: 'Offne diese Seite in Safari.',
    stepShare: 'Tippe auf Teilen.',
    stepHome: 'Wahle Zum Home-Bildschirm.',
    stepLaunch: 'Offne {{appName}} uber das neue Icon.',
  },
};

const resolveCoachmarkLocale = (language?: string): SupportedLocale => {
  const normalized = String(language || '').toLowerCase();
  if (normalized.startsWith('fr')) return 'fr';
  if (normalized.startsWith('pt')) return 'pt';
  if (normalized.startsWith('it')) return 'it';
  if (normalized.startsWith('de')) return 'de';
  if (normalized.startsWith('en')) return 'en';
  return 'es';
};

const DEFAULT_APP_NAME = 'OrgCloud';

const extractClientName = (value: unknown): string => {
  if (!value || typeof value !== 'object') return '';
  const record = value as Record<string, any>;
  const candidates = [
    record.name,
    record.Name,
    record.ClientName,
    record.clientName,
    record.companyName,
    record.Information?.Name,
    record.Information?.CompanyName,
    record.Data?.Name,
  ];

  const name = candidates.find((candidate) => typeof candidate === 'string' && candidate.trim().length > 0);
  return typeof name === 'string' ? name.trim() : '';
};

const resolveInstalledAppName = (): string => {
  if (typeof window === 'undefined') return DEFAULT_APP_NAME;

  try {
    const readNameFromKey = (key: string | null): string => {
      if (!key) return '';
      const rawValue = window.localStorage.getItem(key);
      if (!rawValue) return '';
      try {
        return extractClientName(JSON.parse(rawValue));
      } catch {
        return '';
      }
    };

    const knownClientIds = [
      window.localStorage.getItem('lastClientId'),
      window.localStorage.getItem('selectedClientId'),
      window.localStorage.getItem('currentClientId'),
    ].filter(Boolean) as string[];

    for (const clientId of knownClientIds) {
      const companyName = readNameFromKey(`${clientId}_company`);
      if (companyName) return companyName;
    }

    for (const key of ['companyContext', 'currentCompany', 'selectedCompany', 'clientContext']) {
      const companyName = readNameFromKey(key);
      if (companyName) return companyName;
    }

    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (!key?.endsWith('_company')) continue;
      const companyName = readNameFromKey(key);
      if (companyName) return companyName;
    }
  } catch {
    return DEFAULT_APP_NAME;
  }

  return DEFAULT_APP_NAME;
};

const personalizeCoachmarkCopy = (copy: CoachmarkCopy, appName: string): CoachmarkCopy =>
  Object.entries(copy).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: value.replace(/\{\{appName\}\}/g, appName || DEFAULT_APP_NAME),
    }),
    {} as CoachmarkCopy,
  );

const glow = keyframes`
  0%, 100% { opacity: 0.46; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.08); }
`;

export default function PWAInstallCoachmark({ enabled = true }: PWAInstallCoachmarkProps) {
  const { i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isIosGuide, setIsIosGuide] = useState(false);
  const [requiresSafari, setRequiresSafari] = useState(false);
  const [showSteps, setShowSteps] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<DeferredInstallPromptEvent | null>(null);
  const [appName, setAppName] = useState(DEFAULT_APP_NAME);

  const copy = useMemo(
    () => personalizeCoachmarkCopy(PWA_COPY[resolveCoachmarkLocale(i18n.language)], appName),
    [appName, i18n.language],
  );
  const cardBg = useColorModeValue('rgba(255,255,255,0.92)', 'rgba(15, 23, 42, 0.92)');
  const cardBorder = useColorModeValue('rgba(148, 163, 184, 0.28)', 'rgba(148, 163, 184, 0.20)');
  const stepBg = useColorModeValue('rgba(248,250,252,0.92)', 'rgba(255,255,255,0.055)');
  const textColor = useColorModeValue('slate.900', 'white');
  const muted = useColorModeValue('slate.600', 'whiteAlpha.760');
  const accent = useColorModeValue('#2563eb', '#67e8f9');
  const accentSoft = useColorModeValue('rgba(37,99,235,0.12)', 'rgba(103,232,249,0.14)');

  useEffect(() => {
    setAppName(resolveInstalledAppName());
  }, []);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      setIsVisible(false);
      return;
    }

    const coachmarkState = getPwaInstallCoachmarkState();
    if (coachmarkState === 'hidden' || coachmarkState === 'installed') {
      setIsVisible(false);
      return;
    }

    const ua = String(window.navigator.userAgent || '');
    const isStandalone = isInstalledAppRuntime();
    if (isStandalone) {
      markPwaInstalled();
      setIsVisible(false);
      return;
    }

    const isMobileViewport = window.matchMedia?.('(max-width: 860px)').matches === true;
    const isCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches === true;
    const isMobileLike = isMobileViewport || isCoarsePointer || /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
    if (!isMobileLike) {
      setIsVisible(false);
      return;
    }

    const isIos = /iPhone|iPad|iPod/i.test(ua);
    const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser|SamsungBrowser/i.test(ua);
    let visibilityTimeout = 0;
    if (isIos) {
      setIsIosGuide(true);
      setRequiresSafari(!isSafari);
      visibilityTimeout = window.setTimeout(() => setIsVisible(true), 900);
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as DeferredInstallPromptEvent);
      setIsIosGuide(false);
      setRequiresSafari(false);
      setIsVisible(true);
    };

    const handleAppInstalled = () => {
      markPwaInstalled();
      setIsVisible(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      if (visibilityTimeout) window.clearTimeout(visibilityTimeout);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [enabled]);

  const hideCoachmark = () => {
    hidePwaInstallCoachmark();
    setIsVisible(false);
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    try {
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === 'accepted') {
        markPwaInstalled();
        setIsVisible(false);
      }
    } finally {
      setDeferredPrompt(null);
    }
  };

  if (!isVisible) return null;

  const steps = isIosGuide
    ? [
        requiresSafari ? copy.stepSafari : copy.stepShare,
        copy.stepHome,
        copy.stepLaunch,
      ]
    : [copy.nativeHint];

  return (
    <Flex
      position="fixed"
      insetX={{ base: 0, md: 'auto' }}
      right={{ base: 'auto', md: 4 }}
      bottom={{ base: 'calc(10px + env(safe-area-inset-bottom, 0px))', md: 4 }}
      px={{ base: 3, md: 0 }}
      zIndex={3600}
      justify={{ base: 'center', md: 'flex-end' }}
      pointerEvents="none"
    >
      <Box
        pointerEvents="auto"
        w={{ base: '100%', md: '340px' }}
        maxW="100%"
        borderRadius={{ base: '20px', md: '22px' }}
        border="1px solid"
        borderColor={cardBorder}
        bg={cardBg}
        backdropFilter="blur(22px) saturate(180%)"
        boxShadow="0 18px 48px rgba(2, 6, 23, 0.30)"
        overflow="hidden"
      >
        <HStack align="start" spacing={3} p={{ base: 3, md: 3.5 }}>
          <Circle size="36px" bg={accentSoft} color={accent} flexShrink={0} position="relative">
            <Circle position="absolute" inset="5px" bg={accentSoft} animation={`${glow} 2.6s ease-in-out infinite`} />
            <Icon as={isIosGuide ? FiShare : MdInstallMobile} boxSize={4.5} position="relative" />
          </Circle>
          <VStack align="stretch" spacing={1} flex="1" minW={0}>
            <Text fontSize="xs" color={accent} fontWeight="900" letterSpacing="0.08em" textTransform="uppercase">
              {copy.badge}
            </Text>
            <Text color={textColor} fontSize="sm" fontWeight="850" lineHeight="short">
              {copy.title}
            </Text>
            <Text color={muted} fontSize="xs" lineHeight="1.4" noOfLines={2}>
              {isIosGuide ? (requiresSafari ? copy.safariHint : copy.installedHint) : copy.subtitle}
            </Text>
          </VStack>
          <IconButton
            aria-label={copy.dismiss}
            icon={<CloseIcon boxSize={2.5} />}
            size="sm"
            minW="32px"
            h="32px"
            borderRadius="full"
            variant="ghost"
            color={muted}
            onClick={hideCoachmark}
          />
        </HStack>

        <Collapse in={showSteps} animateOpacity>
          <VStack align="stretch" spacing={2} px={{ base: 3.5, md: 4 }} pb={3}>
            {steps.map((step, index) => (
              <HStack key={step} spacing={2.5} p={2.5} borderRadius="16px" bg={stepBg}>
                <Circle size="24px" bg={accentSoft} color={accent} fontSize="xs" fontWeight="900">
                  {index + 1}
                </Circle>
                <Text color={muted} fontSize="xs" fontWeight="650">
                  {step}
                </Text>
              </HStack>
            ))}
          </VStack>
        </Collapse>

        <HStack
          spacing={2}
          px={{ base: 3, md: 3.5 }}
          pb={{ base: 3, md: 3.5 }}
          pt={showSteps ? 0 : 1}
          justify="space-between"
        >
          <Button
            variant="ghost"
            size="sm"
            borderRadius="full"
            color={muted}
            rightIcon={<Icon as={showSteps ? MdKeyboardArrowUp : MdKeyboardArrowDown} />}
            onClick={() => setShowSteps((value) => !value)}
          >
            {showSteps ? copy.hideDetails : copy.details}
          </Button>
          {deferredPrompt && !isIosGuide ? (
            <Button size="sm" borderRadius="full" colorScheme="brand" onClick={handleInstall}>
              {copy.installButton}
            </Button>
          ) : (
            <Button size="sm" borderRadius="full" variant="outline" color={textColor} onClick={hideCoachmark}>
              {copy.later}
            </Button>
          )}
        </HStack>
      </Box>
    </Flex>
  );
}
